import { useState } from "react";
import { Search, Send, Loader2, Heart, Globe, TrendingUp, Zap, Users, Eye, AlertCircle, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { trackEvent } from "../../lib/track";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SUGGESTIONS = [
  "Show me all care-oriented AI in Africa",
  "What healthcare AI exists in India?",
  "Female-founded AI in education",
  "Extraction-oriented apps in security",
  "Physical AI devices for elderly care",
  "AI apps from Latin America",
  "What's being built for climate action?",
  "Creative AI tools from Europe",
];

const SCENARIOS = [
  { query: "Can a pregnant woman in rural India access AI that helps her work and stay healthy?", label: "Women & Work", icon: Users, color: "#ec4899" },
  { query: "What AI helps the poor create livelihoods and micro-entrepreneurship in developing countries?", label: "Economic Mobility", icon: TrendingUp, color: "#f59e0b" },
  { query: "Show me AI that helps communities adapt to climate change, not just corporations measuring ESG", label: "Climate Resilience", icon: Globe, color: "#10b981" },
  { query: "What AI brings quality education to children without schools, teachers, or internet?", label: "Education for All", icon: Search, color: "#6366f1" },
  { query: "Does AI reach the last mile for rural diagnostics, maternal health, and mental health for those who cannot afford therapy?", label: "Health Equity", icon: Heart, color: "#ef4444" },
  { query: "What physical AI devices exist for people with disabilities?", label: "Accessibility", icon: Eye, color: "#06b6d4" },
];

const orientColor = { care: "#10b981", utility: "#3b82f6", creative: "#8b5cf6", extraction: "#ef4444" };

export const DeepQuery = () => {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportName, setReportName] = useState("");
  const [reportDesc, setReportDesc] = useState("");
  const [reportSending, setReportSending] = useState(false);

  const submitReport = async () => {
    if (!reportName.trim()) return;
    setReportSending(true);
    try {
      const r = await axios.post(`${API}/report-missing`, { product_name: reportName.trim(), description: reportDesc.trim(), reporter_context: query });
      toast.success(`${reportName} flagged as ${r.data.orientation}. Thank you!`);
      trackEvent("report_missing", "deep_query", reportName.trim());
      setShowReport(false); setReportName(""); setReportDesc("");
    } catch { toast.error("Failed to submit. Please try again."); }
    setReportSending(false);
  };

  const search = async (q) => {
    const searchQuery = q || query;
    if (!searchQuery.trim()) return;
    setLoading(true);
    setResult(null);
    trackEvent("deep_query", "insights", searchQuery.trim().slice(0, 100));
    try {
      const r = await axios.post(`${API}/deep-query`, { query: searchQuery.trim() }, { timeout: 90000 });
      setResult(r.data);
    } catch { setResult({ error: "Query failed. Please try again." }); }
    setLoading(false);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="deep-query">
      {/* Search Input — TOP */}
      <div className="relative overflow-hidden rounded-2xl p-6 md:p-8"
        style={{ background: "linear-gradient(135deg, #0f0a1e 0%, #0c1222 40%, #0a1628 100%)", border: "1px solid rgba(99,102,241,0.15)" }}>
        <p className="text-[10px] font-bold text-indigo-400/70 uppercase tracking-[0.25em] mb-2">AI Insights</p>
        <h1 className="text-xl md:text-2xl font-bold text-white mb-4">Ask the Data Anything</h1>
        <div className="relative max-w-3xl">
          <input value={query} onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") search(); }}
            placeholder="e.g., Show me all care-oriented AI in Africa..."
            className="w-full px-5 py-4 bg-black/50 border border-white/[0.08] rounded-2xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/30 pr-28"
            data-testid="deep-query-input" />
          <button onClick={() => search()} disabled={!query.trim() || loading}
            className="absolute top-1/2 -translate-y-1/2 right-3 flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-400 hover:to-indigo-500 disabled:opacity-30 text-white text-xs font-bold rounded-xl transition-all"
            data-testid="deep-query-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            Query
          </button>
        </div>
        {/* Quick suggestions */}
        <div className="flex flex-wrap gap-2 mt-3 max-w-3xl">
          {SUGGESTIONS.map((s, i) => (
            <button key={i} onClick={() => { setQuery(s); search(s); }}
              className="text-[10px] px-2.5 py-1 rounded-full bg-white/[0.03] border border-white/[0.06] text-slate-400 hover:text-white hover:border-indigo-500/30 transition-all">
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Scenario Cards — explore real-world challenges */}
      {!result && !loading && (
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-3">Explore Real-World Scenarios</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {SCENARIOS.map((s, i) => {
              const Icon = s.icon;
              return (
                <button key={s.label} onClick={() => { setQuery(s.query); search(s.query); }} disabled={loading}
                  className="text-left rounded-xl p-4 hover:scale-[1.02] transition-all disabled:opacity-50"
                  style={{ background: `${s.color}06`, border: `1px solid ${s.color}18` }}>
                  <Icon className="w-4 h-4 mb-2" style={{ color: s.color }} />
                  <h3 className="text-xs font-bold text-white mb-1">{s.label}</h3>
                  <p className="text-[10px] text-slate-400 leading-relaxed line-clamp-2">{s.query}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mx-auto mb-3" />
          <p className="text-xs text-slate-400 animate-pulse">Querying the observatory</p>
        </div>
      )}

      {/* Results */}
      {result && !result.error && (
        <div className="space-y-4 animate-fade-in">
          {/* Headline + Key Stat */}
          <div className="rounded-2xl p-6" style={{ background: "rgba(99,102,241,0.04)", border: "1px solid rgba(99,102,241,0.15)" }}>
            <p className="text-[10px] text-indigo-400 mb-2">{result.parsed_question}</p>
            <h2 className="text-xl font-bold text-white mb-3">{result.headline}</h2>
            <div className="flex flex-wrap gap-6 mb-4">
              <div className="text-center">
                <p className="text-3xl font-black text-white">{result.total?.toLocaleString()}</p>
                <p className="text-[10px] text-slate-400">entities found</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-black text-emerald-400">{result.care_pct}%</p>
                <p className="text-[10px] text-slate-400">care-oriented</p>
              </div>
              {result.entity_types && Object.entries(result.entity_types).map(([k, v]) => (
                <div key={k} className="text-center">
                  <p className="text-2xl font-black text-slate-300">{v.toLocaleString()}</p>
                  <p className="text-[10px] text-slate-400">{k === "application" ? "software" : k}s</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">{result.analysis}</p>
            {result.key_insight && (
              <div className="mt-3 px-4 py-2 bg-indigo-500/[0.08] border border-indigo-500/15 rounded-lg">
                <p className="text-xs text-indigo-300 font-medium">{result.key_insight}</p>
              </div>
            )}
          </div>

          {/* Orientation Breakdown */}
          {result.orientations && (
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Value Orientation</p>
              <div className="flex gap-4">
                {Object.entries(result.orientations).filter(([k]) => k).map(([orient, count]) => {
                  const pct = Math.round(count / result.total * 100);
                  return (
                    <div key={orient} className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] capitalize font-bold" style={{ color: orientColor[orient] || "#94a3b8" }}>{orient}</span>
                        <span className="text-[10px] text-slate-400">{pct}%</span>
                      </div>
                      <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: orientColor[orient] || "#94a3b8" }} />
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1">{count.toLocaleString()}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Countries + Industries side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {result.countries?.length > 0 && (
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Top Countries</p>
                <div className="space-y-1.5">
                  {result.countries.slice(0, 8).map((c) => {
                    const max = result.countries[0]?.count || 1;
                    return (
                      <div key={c.country} className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400 w-20 truncate">{c.country}</span>
                        <div className="flex-1 h-2 bg-white/[0.04] rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-indigo-400/60" style={{ width: `${(c.count / max) * 100}%` }} />
                        </div>
                        <span className="text-[10px] text-slate-400 w-10 text-right">{c.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {result.industries?.length > 0 && (
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Industries</p>
                <div className="space-y-1.5">
                  {result.industries.slice(0, 8).map((ind) => {
                    const max = result.industries[0]?.count || 1;
                    return (
                      <div key={ind.industry} className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400 w-28 truncate">{ind.industry}</span>
                        <div className="flex-1 h-2 bg-white/[0.04] rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-violet-400/60" style={{ width: `${(ind.count / max) * 100}%` }} />
                        </div>
                        <span className="text-[10px] text-slate-400 w-8 text-right">{ind.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Products */}
          {result.products?.length > 0 && (
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Matching Products ({result.total > 20 ? `showing 20 of ${result.total.toLocaleString()}` : result.total})</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {result.products.map((p) => (
                  <div key={p.product_name} className="flex items-start gap-2 bg-white/[0.02] rounded-lg p-2.5 border border-white/[0.03]">
                    <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: orientColor[p.value_orientation] || "#94a3b8" }} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-bold text-white">{p.product_name}</span>
                        {p.sub_type && <span className="text-[8px] px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-400 uppercase">{p.sub_type}</span>}
                      </div>
                      <p className="text-[10px] text-slate-400 truncate">{(p.tagline || "").slice(0, 70)}</p>
                      <p className="text-[10px] text-slate-500">{p.country} {p.industry ? `· ${p.industry}` : ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Research Papers */}
          {result.research_papers?.length > 0 && (
            <div className="bg-gradient-to-b from-cyan-500/[0.03] to-transparent border border-cyan-500/10 rounded-xl p-4" data-testid="dq-research-papers">
              <p className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider mb-3">
                Related Research ({result.research_total > 10 ? `showing 10 of ${result.research_total.toLocaleString()}` : result.research_total} papers)
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {result.research_papers.map((p) => (
                  <div key={p.paper_title} className="flex items-start gap-2 bg-white/[0.02] rounded-lg p-2.5 border border-white/[0.03]">
                    <div className="w-2 h-2 rounded-[2px] mt-1.5 flex-shrink-0 rotate-45" style={{ background: orientColor[p.value_orientation] || "#94a3b8" }} />
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold text-white line-clamp-2">{p.paper_title}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {p.year && <span className="text-[9px] text-slate-500">{p.year}</span>}
                        {p.citations > 0 && <span className="text-[9px] text-cyan-400/60">{p.citations} cites</span>}
                        {p.institution && p.institution !== "Unknown" && <span className="text-[9px] text-slate-500 truncate">{p.institution}</span>}
                        <span className="text-[8px] px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-400 uppercase">{p.source === "semantic_scholar" ? "S2" : "arXiv"}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Founders */}
          {result.founders && Object.keys(result.founders).length > 0 && (
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Founder Composition (known)</p>
              <div className="flex gap-4">
                {Object.entries(result.founders).map(([k, v]) => (
                  <div key={k} className="text-center">
                    <p className="text-lg font-black text-white">{v}</p>
                    <p className="text-[10px] text-slate-400 capitalize">{k.replace("_", " ")}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nudge to Possibility Engine */}
          <div className="rounded-xl p-5 text-center" style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.04) 0%, rgba(139,92,246,0.04) 100%)", border: "1px solid rgba(245,158,11,0.12)" }}>
            <p className="text-xs text-amber-400 font-bold mb-1">Don't see what you're looking for?</p>
            <p className="text-[11px] text-slate-400 mb-3">The Possibility Engine can imagine what doesn't exist yet. It maps how existing software and hardware AI could converge to solve this challenge.</p>
            <button onClick={() => {
              window.dispatchEvent(new CustomEvent("navigate-tab", { detail: "c-possibility" }));
            }}
              className="px-5 py-2 bg-gradient-to-r from-amber-500/20 to-violet-500/20 hover:from-amber-500/30 hover:to-violet-500/30 border border-amber-500/20 text-amber-300 text-xs font-bold rounded-xl transition-all"
              data-testid="nudge-possibility">
              Dream it in the Possibility Engine
            </button>
          </div>

          {/* Report Missing Data */}
          <div className="rounded-xl p-4 text-center" style={{ background: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.12)" }}>
            <p className="text-xs text-slate-400 mb-2">Know an AI product missing from our database?</p>
            <button onClick={() => setShowReport(true)} data-testid="report-missing-btn"
              className="px-4 py-2 bg-red-500/15 hover:bg-red-500/25 border border-red-500/20 text-red-300 text-xs font-bold rounded-xl transition-all flex items-center gap-2 mx-auto">
              <AlertCircle className="w-3.5 h-3.5" /> Report Missing Data
            </button>
          </div>
        </div>
      )}

      {/* Report Missing Modal */}
      {showReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowReport(false)}>
          <div className="bg-[#0a1628] border border-white/[0.1] rounded-2xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-1">Report Missing AI Product</h3>
            <p className="text-xs text-slate-400 mb-4">Help us improve the observatory. We'll classify the orientation and add it after review.</p>
            <input value={reportName} onChange={e => setReportName(e.target.value)} placeholder="Product or company name"
              className="w-full px-4 py-3 bg-black/40 border border-white/[0.08] rounded-xl text-sm text-white placeholder:text-slate-500 mb-3 focus:outline-none focus:border-indigo-500/30" data-testid="report-name" />
            <textarea value={reportDesc} onChange={e => setReportDesc(e.target.value)} placeholder="Brief description: what does it do? (optional)" rows={2}
              className="w-full px-4 py-3 bg-black/40 border border-white/[0.08] rounded-xl text-sm text-white placeholder:text-slate-500 mb-4 resize-none focus:outline-none focus:border-indigo-500/30" data-testid="report-desc" />
            <div className="flex gap-3">
              <button onClick={() => setShowReport(false)} className="flex-1 px-4 py-2 text-xs text-slate-400 hover:text-slate-300 border border-white/[0.06] rounded-xl transition-all">Cancel</button>
              <button onClick={submitReport} disabled={!reportName.trim() || reportSending} data-testid="report-submit"
                className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-400 hover:to-indigo-500 disabled:opacity-30 text-white text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2">
                {reportSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {reportSending ? "Submitting..." : "Report"}
              </button>
            </div>
          </div>
        </div>
      )}

      {result?.error && <p className="text-sm text-red-400 text-center py-8">{result.error}</p>}
    </div>
  );
};

export default DeepQuery;
